import { useMemo, useCallback, useEffect, useState } from 'react';
import { Col, Row, Form } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'react-router-dom';
import { useApi } from 'hooks/useApi';
import moment from 'moment';
import RoomsApi from 'libs/api/leasing/room';
import { useFilter } from 'hooks/useFilter';
import AssetApi from 'libs/api/leasing/asset';

const api = new RoomsApi();
const assetApi = new AssetApi();
/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{onChangeTableFilter: (function(*, *, *): void), data: {pagination: *&{current: number, hideOnSinglePage: boolean, pageSize: number, position: [string], showSizeChanger: boolean}, sorting: {field: string, order: string}}, columns: [{dataIndex: string, fixed: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},{dataIndex: string, title: TFuncReturn<"translation", string, string, undefined>},null], handleDownloadExcel: ((function(): Promise<void>)|*), singleItem: {}, filters: [{values: {title: *, value: *}[], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},{values: [{title: string, value: string},{title: string, value: string}], name: string, title: TFuncReturn<"translation", string, string, undefined>, type: string},null,null,null], loading: boolean, onDelete: ((function(*): Promise<void>)|*), updateInProcess: boolean, companies: ([]|*[]), form: FormInstance<any>, handleCreateOrUpdate: ((function(*): Promise<void>)|*), asset: *[]}} state and callbacks for ExperienceYears page
 */

const useRoom = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { filterData } = useFilter();
  const [asset, setAsset] = useState([]);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const pages = useRecoilValue(getPage);

  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
  } = useApi({
    resourceKey: 'rooms',
    singleResourceKey: 'room',
    api,
    isMainPage,
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Asset'),
        dataIndex: 'leasings_asset',
        fixed: 'left',
        sorter: true,
      },
      {
        title: t('Unità'),
        dataIndex: 'title',
        sorter: true,
      },
      {
        title: t('GLA'),
        dataIndex: 'gla',
        sorter: true,
      },
      // {
      //   title: t('Sales area'),
      //   dataIndex: 'company_title',
      // },
      {
        title: t('Stato unità'),
        dataIndex: 'status',
        sorter: true,
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row = { can_update: false, can_destroy: false }) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);

      const data = {
        room: {
          ...values,
        },
      };

      try {
        if (!id) {
          await api.create({ data });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, { data });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response.data || {};
        if (errors.status !== 500) {
          form.setFields(
            ErrorHelper.getErrorsForForm(errors, {
              leasings_contact_role: 'leasings_contact_role_id',
            }),
          );
        }
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess, form, navigate, pathname, id],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem) {
      let fieldsValues = [];
      for (let key in singleItem) {
        switch (key) {
          case 'lasttime_contact_date':
          case 'first_visit_date': {
            fieldsValues.push({
              name: key,
              value: moment(singleItem[key], 'YYYY-MM-DD'),
            });
            break;
          }
          default: {
            fieldsValues.push({
              name: key,
              value: singleItem[key],
            });
          }
        }
      }
      form.setFields(fieldsValues);
    }
  }, [singleItem, form, id, isMainPage]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { assets },
        } = await assetApi.list({
          params: { per: 1000 },
        });
        setAsset(assets);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const filters = useMemo(
    () => [
      {
        title: t('Asset'),
        type: 'select',
        name: 'by_leasings_asset_id',
        values: asset.map((item) => ({
          title: item.title,
          value: item.id,
        })),
      },
      {
        title: t('Status'),
        type: 'select',
        name: 'by_status',
        values: [
          {
            title: 'void',
            value: 'void',
          },
          {
            title: 'open',
            value: 'open',
          },
          {
            title: 'in_asset',
            value: 'in_asset',
          },
        ],
      },
      {
        title: t('Impianto a/c'),
        type: 'select',
        name: 'by_plant_ac',
        values: [
          {
            title: 'Presente',
            value: 'present',
          },
          {
            title: 'Assente',
            value: 'absent',
          },
        ],
      },
      {
        title: t('Impianto luci'),
        type: 'select',
        name: 'by_plant_lights',
        values: [
          {
            title: 'Presente',
            value: 'present',
          },
          {
            title: 'Assente',
            value: 'absent',
          },
        ],
      },
      {
        title: t('Antitaccheggio'),
        type: 'select',
        name: 'by_plant_anti_theft',
        values: [
          {
            title: 'Presente',
            value: 'present',
          },
          {
            title: 'Assente',
            value: 'absent',
          },
        ],
      },
      {
        title: t('Bagno'),
        type: 'select',
        name: 'by_wc',
        values: [
          {
            title: 'Presente',
            value: 'present',
          },
          {
            title: 'Assente',
            value: 'absent',
          },
        ],
      },
      {
        title: t('Arredo'),
        type: 'select',
        name: 'by_furniture',
        values: [
          {
            title: 'Presente',
            value: 'present',
          },
          {
            title: 'Assente',
            value: 'absent',
          },
        ],
      },
      {
        title: t('RANGE MQ'),
        type: 'select',
        name: 'by_gla_range',
        values: [
          {
            title: '0-150',
            value: '0-150',
          },
          {
            title: '150-230',
            value: '150-230',
          },
          {
            title: '230-300',
            value: '230-300',
          },
          {
            title: '300-MAX',
            value: '300-MAX',
          },
        ],
      },
      {
        title: t('Finitura'),
        type: 'select',
        name: 'by_finish',
        values: [
          {
            title: 'Rustico',
            value: 'rustic',
          },
          {
            title: 'Finita',
            value: 'fake',
          },
        ],
      },
    ],
    [t, asset],
  );

  return {
    form,
    columns,
    data,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    handleCreateOrUpdate,
    onChangeTableFilter,
    asset,
    filters,
    handleDownloadExcel,
    companies: filterData?.companies || [],
    inactiveCompanies: filterData?.inactive_companies || [],
  };
};

export default useRoom;
